

import React, { useState, useEffect } from 'react';
import { Language } from '../types';
import { TRANSLATIONS } from '../translations';
import { PageVariant } from '../types';

interface CalculatorProps {
  language: Language;
}

export const Calculator: React.FC<CalculatorProps> = ({ language }) => {
  const [size, setSize] = useState<number>(180);
  const [finish, setFinish] = useState<'standard' | 'premium' | 'luxury'>('premium');
  const [site, setSite] = useState<'flat' | 'sloped' | 'complex'>('flat');
  const [zone, setZone] = useState<'metro' | 'inner' | 'coastal' | 'regional'>('metro');
  const [locationInput, setLocationInput] = useState('');
  
  // State for the breakdown
  const [breakdown, setBreakdown] = useState({
    moduleCost: 0,
    siteCost: 0,
    logisticsCost: 0,
    total: 0,
    perSqm: 0
  });

  const t = TRANSLATIONS[language].footer;

  // --- PRICING LOGIC ---
  // Target: $1,800 - $2,500 blended rate (including foundations)

  // 1. SITE BASE RATE (The "Foundation" portion)
  // We assume a base foundation cost of ~$350/m2 for flat ground.
  const siteBaseRatePerSqm = 350; 

  // 2. MODULE RATES (The "House" portion)
  // Calculated to ensure (Module + Site) hits the target bands.
  const moduleRates = {
    standard: 1450, // $1450 + $350 = $1,800/m2 (Target Low Band)
    premium: 2150,  // $2150 + $350 = $2,500/m2 (Target High Band)
    luxury: 0       // Handled separately as "Quotation"
  };

  // Multipliers for Topography difficulty on the SITE portion only
  const siteDifficultyFactors = {
    flat: 1.0,      // $350/m2
    sloped: 1.5,    // $525/m2 (Retaining, deeper piles)
    complex: 2.5    // $875/m2 (Engineering heavy)
  };

  // 3. LOGISTICS & ACCESS RATES (Per m²)
  const zoneRatesPerSqm = {
    metro: 0,       // Included in base
    inner: 350,     // Traffic control, tight access
    coastal: 250,   // Marine grade, travel allowance
    regional: 400   // Distance, crew accommodation
  };

  const zoneLabels = {
    metro: { label: 'Metro Melbourne', desc: 'Standard Access' },
    inner: { label: 'Inner City', desc: 'Tight Access / Traffic Control' },
    coastal: { label: 'Coastal / Peninsula', desc: 'Marine Grade / Travel' },
    regional: { label: 'Regional Vic', desc: 'Long Haul / Crew Allowance' }
  };

  useEffect(() => {
    calculate();
  }, [size, finish, site, zone]);

  const calculate = () => {
    if (finish === 'luxury') {
      // Logic handled in render
      return;
    }

    // 1. The House
    const baseModuleCost = size * moduleRates[finish];
    
    // 2. The Ground
    const estimatedSiteCost = (size * siteBaseRatePerSqm) * siteDifficultyFactors[site];
    
    // 3. The Logistics
    const estimatedLogistics = size * zoneRatesPerSqm[zone];

    const total = baseModuleCost + estimatedSiteCost + estimatedLogistics;

    setBreakdown({
      moduleCost: baseModuleCost,
      siteCost: estimatedSiteCost,
      logisticsCost: estimatedLogistics,
      total: total,
      perSqm: Math.round(total / size)
    });
  };

  const handleLocationCheck = () => {
    const input = locationInput.toLowerCase().trim();
    if (!input) return;

    let detected = false;
    const postcodeMatch = input.match(/\b(3\d{3})\b/);
    if (postcodeMatch) {
      const p = parseInt(postcodeMatch[1], 10);
      if ((p >= 3000 && p <= 3051) || (p >= 3205 && p <= 3207)) { setZone('inner'); detected = true; }
      else if ((p >= 3910 && p <= 3999) || (p >= 3220 && p <= 3230) || (p >= 3926 && p <= 3944)) { setZone('coastal'); detected = true; }
      else if ((p >= 3000 && p <= 3204) || (p >= 3800 && p <= 3810)) { setZone('metro'); detected = true; }
      else if (p >= 3211 && p <= 3999) { setZone('regional'); detected = true; }
    }

    if (!detected) {
      const innerSuburbs = ['melbourne', 'southbank', 'docklands', 'carlton', 'fitzroy', 'collingwood', 'richmond', 'south yarra', 'prahran', 'st kilda', 'albert park'];
      const coastalSuburbs = ['sorrento', 'portsea', 'rye', 'rosebud', 'dromana', 'mornington', 'torquay', 'anglesea', 'lorne', 'ocean grove', 'barwon heads', 'flinders', 'red hill'];
      const regionalSuburbs = ['geelong', 'ballarat', 'bendigo', 'shepparton', 'traralgon', 'warrnambool', 'mildura', 'echuca', 'daylesford'];

      if (innerSuburbs.some(s => input.includes(s))) { setZone('inner'); detected = true; }
      else if (coastalSuburbs.some(s => input.includes(s))) { setZone('coastal'); detected = true; }
      else if (regionalSuburbs.some(s => input.includes(s))) { setZone('regional'); detected = true; }
    }
  };

  const formatMoney = (amount: number) => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD',
      maximumFractionDigits: 0,
    }).format(amount);
  };

  return (
    <div className="pt-32 lg:pt-36 bg-slate-50 min-h-screen">
      <section className="py-24">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-[10px] font-black text-blue-600 uppercase tracking-[0.4em] mb-4">{t.col1}</h2>
            <h1 className="text-4xl lg:text-5xl font-black text-slate-900 mb-6 tracking-tighter">Project Feasibility Estimator</h1>
            <p className="text-lg text-slate-500 max-w-2xl mx-auto">
              Modular construction shifts cost from "unpredictable site labor" to "fixed factory production". 
              Get a verified feasibility estimate below.
            </p>
          </div>

          <div className="grid lg:grid-cols-12 gap-8 items-start">
            
            {/* Input Panel */}
            <div className="lg:col-span-5 bg-white p-8 lg:p-10 rounded-[2.5rem] shadow-xl border border-slate-200 space-y-10">
              
              {/* Size Input */}
              <div>
                <div className="flex justify-between items-end mb-4">
                  <label className="text-xs font-bold text-slate-400 uppercase tracking-widest">Floor Area</label>
                  <span className="text-2xl font-black text-slate-900">{size} m²</span>
                </div>
                <input 
                  type="range" 
                  min="60" 
                  max="600" 
                  step="10" 
                  value={size} 
                  onChange={(e) => setSize(Number(e.target.value))}
                  className="w-full h-2 bg-slate-100 rounded-lg appearance-none cursor-pointer accent-blue-600"
                />
                <div className="flex justify-between mt-2 text-[10px] text-slate-400 font-bold uppercase tracking-wider">
                  <span>Tiny Home (60)</span>
                  <span>Estate (600)</span>
                </div>
              </div>

              {/* Specification Level */}
              <div>
                <label className="block text-xs font-bold text-slate-400 uppercase tracking-widest mb-4">Specification Level</label>
                <div className="space-y-3">
                  {[
                    { id: 'standard', label: 'Standard', price: '~ $1,800/m²', desc: 'Includes Base Foundation + Standard Finishes' },
                    { id: 'premium', label: 'Premium', price: '~ $2,500/m²', desc: 'Includes Base Foundation + Arch. Finishes' },
                    { id: 'luxury', label: 'Luxury', price: 'Custom Quote', desc: 'Bespoke design, automation, complex form.' }
                  ].map((lvl) => (
                    <button
                      key={lvl.id}
                      onClick={() => setFinish(lvl.id as any)}
                      className={`w-full text-left p-4 rounded-xl border transition-all duration-200 group ${
                        finish === lvl.id 
                        ? 'bg-slate-900 border-slate-900 text-white shadow-lg' 
                        : 'bg-white border-slate-200 hover:border-blue-400'
                      }`}
                    >
                      <div className="flex justify-between items-center mb-1">
                        <span className="font-bold uppercase tracking-wider text-xs">{lvl.label}</span>
                        <span className={`text-xs font-bold ${finish === lvl.id ? 'text-blue-400' : 'text-slate-400'}`}>{lvl.price}</span>
                      </div>
                      <p className={`text-xs ${finish === lvl.id ? 'text-slate-400' : 'text-slate-500'}`}>{lvl.desc}</p>
                    </button>
                  ))}
                </div>
              </div>

              {/* Location Zone */}
              <div>
                <label className="block text-xs font-bold text-slate-400 uppercase tracking-widest mb-4">Project Zone & Access</label>
                <div className="mb-4 flex gap-2">
                  <input 
                    type="text" 
                    value={locationInput}
                    onChange={(e) => setLocationInput(e.target.value)}
                    placeholder="Suburb or Postcode"
                    onKeyDown={(e) => e.key === 'Enter' && handleLocationCheck()}
                    className="flex-1 px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none transition-all placeholder:text-slate-300 text-sm font-bold text-slate-900"
                  />
                  <button 
                    onClick={handleLocationCheck}
                    className="px-4 bg-slate-900 text-white rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-blue-600 transition-colors shadow-lg shadow-slate-900/10"
                  >
                    Check
                  </button>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  {Object.entries(zoneLabels).map(([key, data]) => (
                    <button
                      key={key}
                      onClick={() => setZone(key as any)}
                      className={`p-3 rounded-xl border text-left transition-all ${
                        zone === key
                        ? 'bg-blue-600 border-blue-600 text-white shadow-md'
                        : 'bg-white border-slate-200 text-slate-500 hover:bg-slate-50'
                      }`}
                    >
                      <div className="font-bold uppercase tracking-wider text-[10px] mb-1">{data.label}</div>
                      <div className={`text-[9px] leading-tight ${zone === key ? 'text-blue-100' : 'text-slate-400'}`}>{data.desc}</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Site Conditions */}
              <div>
                <label className="block text-xs font-bold text-slate-400 uppercase tracking-widest mb-4">Slope & Topography</label>
                <div className="grid grid-cols-3 gap-3">
                  {[
                    { id: 'flat', label: 'Flat', icon: 'fa-minus' },
                    { id: 'sloped', label: 'Sloped', icon: 'fa-chart-area' },
                    { id: 'complex', label: 'Complex', icon: 'fa-mountain' }
                  ].map((s) => (
                    <button
                      key={s.id}
                      onClick={() => setSite(s.id as any)}
                      className={`py-4 px-2 rounded-xl border flex flex-col items-center gap-2 transition-all ${
                        site === s.id 
                        ? 'bg-slate-900 border-slate-900 text-white shadow-md transform scale-105' 
                        : 'bg-white border-slate-200 text-slate-500 hover:bg-slate-50'
                      }`}
                    >
                      <i className={`fa-solid ${s.icon}`}></i>
                      <span className="text-[10px] font-black uppercase tracking-widest">{s.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Results Panel */}
            <div className="lg:col-span-7 space-y-6">
              
              {finish === 'luxury' ? (
                // LUXURY QUOTE VIEW
                <div className="bg-slate-900 text-white p-12 rounded-[2.5rem] shadow-2xl relative overflow-hidden h-full flex flex-col justify-center items-center text-center">
                    <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-blue-600/20 rounded-full blur-[100px] -mr-32 -mt-32"></div>
                    <div className="relative z-10 max-w-md">
                        <div className="w-20 h-20 bg-white/10 rounded-3xl flex items-center justify-center text-3xl mb-8 mx-auto border border-white/10">
                            <i className="fa-solid fa-gem text-blue-400"></i>
                        </div>
                        <h2 className="text-3xl font-black mb-6">Luxury & Bespoke Pricing</h2>
                        <p className="text-slate-400 mb-10 leading-relaxed">
                            Luxury projects involve complex engineering, premium materials, and bespoke architectural details that cannot be accurately estimated by an algorithm.
                        </p>
                        <button className="px-10 py-5 bg-blue-600 hover:bg-blue-700 text-white font-black uppercase tracking-widest text-xs rounded-xl transition-all shadow-lg shadow-blue-600/30">
                            Request Custom Quotation
                        </button>
                    </div>
                </div>
              ) : (
                // STANDARD/PREMIUM CALC VIEW
                <div className="bg-slate-900 text-white p-10 lg:p-12 rounded-[2.5rem] shadow-2xl relative overflow-hidden">
                    <div className="relative z-10">
                    <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-6 mb-10">
                        <div>
                        <p className="text-slate-400 text-xs font-bold uppercase tracking-widest mb-2">Total Estimated Feasibility</p>
                        <div className="text-5xl lg:text-7xl font-black tracking-tighter text-white">
                            {formatMoney(breakdown.total)}
                        </div>
                        </div>
                        <div className="text-right">
                        <p className="text-blue-500 text-xs font-bold uppercase tracking-widest mb-1">Blended Rate</p>
                        <p className="text-2xl font-bold">{formatMoney(breakdown.perSqm)} / m²</p>
                        </div>
                    </div>

                    {/* Breakdown Bars */}
                    <div className="space-y-6">
                        {/* Module Cost Bar */}
                        <div>
                        <div className="flex justify-between text-xs font-bold uppercase tracking-wider mb-2">
                            <span>Le Sector (Module & Manufacture)</span>
                            <span>{formatMoney(breakdown.moduleCost)}</span>
                        </div>
                        <div className="h-4 bg-white/10 rounded-full overflow-hidden">
                            <div 
                            className="h-full bg-blue-500 rounded-full shadow-[0_0_15px_rgba(59,130,246,0.5)] transition-all duration-500"
                            style={{ width: `${(breakdown.moduleCost / breakdown.total) * 100}%` }}
                            ></div>
                        </div>
                        </div>

                        {/* Site Cost Bar */}
                        <div>
                        <div className="flex justify-between text-xs font-bold uppercase tracking-wider mb-2">
                            <span className="flex items-center gap-2">
                            CoreBase (Foundations & Site Works)
                            {site !== 'flat' && <span className="bg-yellow-500/20 text-yellow-400 px-1.5 py-0.5 rounded text-[8px]">COMPLEXITY ADDED</span>}
                            </span>
                            <span className="text-slate-300">{formatMoney(breakdown.siteCost)}</span>
                        </div>
                        <div className="h-4 bg-white/10 rounded-full overflow-hidden mb-2">
                            <div 
                            className="h-full bg-purple-500 rounded-full shadow-[0_0_15px_rgba(168,85,247,0.5)] transition-all duration-500"
                            style={{ width: `${(breakdown.siteCost / breakdown.total) * 100}%` }}
                            ></div>
                        </div>
                        <div className="flex justify-between items-center text-[9px] text-slate-500 italic">
                            <span>Includes screw piles/pads, trenching, and connection.</span>
                            <span className="text-slate-400 font-bold bg-white/10 px-2 py-0.5 rounded">~{formatMoney((size * siteBaseRatePerSqm * siteDifficultyFactors[site]) / size)}/m²</span>
                        </div>
                        </div>

                        {/* Logistics / Zone Loading */}
                        {breakdown.logisticsCost > 0 && (
                        <div className="animate-in fade-in slide-in-from-left-4">
                            <div className="flex justify-between text-xs font-bold uppercase tracking-wider mb-2">
                            <span className="flex items-center gap-2">
                                Logistics & Zone Loading
                                <span className="bg-red-500/20 text-red-400 px-1.5 py-0.5 rounded text-[8px]">{zoneLabels[zone].label.toUpperCase()}</span>
                            </span>
                            <span className="text-slate-300">{formatMoney(breakdown.logisticsCost)}</span>
                            </div>
                            <div className="h-4 bg-white/10 rounded-full overflow-hidden">
                            <div 
                                className="h-full bg-orange-500 rounded-full shadow-[0_0_15px_rgba(249,115,22,0.5)] transition-all duration-500"
                                style={{ width: `${(breakdown.logisticsCost / breakdown.total) * 100}%` }}
                            ></div>
                            </div>
                            <p className="text-[10px] text-slate-400 mt-1 italic">
                            Includes: {zoneLabels[zone].desc}. Calculated at a fixed rate per m².
                            </p>
                        </div>
                        )}
                    </div>
                    </div>

                    {/* Decorative BG */}
                    <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-blue-600/10 rounded-full blur-[100px] -mr-32 -mt-32"></div>
                </div>
              )}

              {/* Disclaimer Card */}
              <div className="bg-white p-8 rounded-[2rem] border border-slate-200">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-blue-50 text-blue-600 rounded-xl flex items-center justify-center shrink-0">
                    <i className="fa-solid fa-circle-info"></i>
                  </div>
                  <div>
                    <h4 className="font-bold text-slate-900 mb-2">What is included?</h4>
                    <p className="text-sm text-slate-500 leading-relaxed mb-4">
                      The blended rates ($1,800 - $2,500/m²) are "Turnkey Feasibility Estimates". They combine the factory module cost and the site foundation cost.
                    </p>
                    <ul className="text-xs text-slate-400 space-y-1 list-disc pl-4 mb-4">
                      <li><strong>Includes:</strong> Module Manufacturing, Standard Foundations (Screw Piles), Installation, Standard Transport.</li>
                      <li><strong>Excludes:</strong> Planning Permits, Council Levies (CIL), Landscaping, Driveways, Utility Connection Fees (Gas, Elec, Water, Comms).</li>
                    </ul>
                    <p className="text-xs font-bold text-red-500">
                      {TRANSLATIONS[language].calculator.disclaimer}
                    </p>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
